import { Bell, Search, Moon, Sun, Menu, X, LayoutDashboard, BookMarked, Award, Calendar, FileText, User, LogOut, Users, GraduationCap, BookOpen, ClipboardList, BarChart3, UserCheck } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getAvatarUrl } from '../../utils/avatar';
import { NavLink } from 'react-router-dom';

const adminMenuItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { path: '/admin/students', icon: Users, label: 'Manage Students' },
  { path: '/admin/lecturers', icon: GraduationCap, label: 'Manage Lecturers' },
  { path: '/admin/subjects', icon: BookOpen, label: 'Subjects' },
  { path: '/admin/exams', icon: ClipboardList, label: 'Exams' },
  { path: '/admin/attendance', icon: Calendar, label: 'Attendance' },
  { path: '/admin/grades', icon: Award, label: 'Grades' },
  { path: '/admin/reports', icon: BarChart3, label: 'Reports & Analytics' },
];

const lecturerMenuItems = [
  { path: '/lecturer', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { path: '/lecturer/subjects', icon: BookOpen, label: 'My Subjects' },
  { path: '/lecturer/students', icon: Users, label: 'My Students' },
  { path: '/lecturer/exams', icon: ClipboardList, label: 'Exams & Marks' },
  { path: '/lecturer/attendance', icon: UserCheck, label: 'Attendance' },
  { path: '/lecturer/reports', icon: BarChart3, label: 'Reports' },
  { path: '/lecturer/profile', icon: User, label: 'My Profile' },
];

const studentMenuItems = [
  { path: '/student', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { path: '/student/subjects', icon: BookMarked, label: 'My Subjects' },
  { path: '/student/results', icon: Award, label: 'Results & Grades' },
  { path: '/student/attendance', icon: Calendar, label: 'Attendance' },
  { path: '/student/exams', icon: FileText, label: 'Upcoming Exams' },
  { path: '/student/profile', icon: User, label: 'My Profile' },
];

export default function Header({ title }) {
  const { user, logout } = useAuth();
  const [darkMode, setDarkMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const getMenuItems = () => {
    switch (user?.roleId) {
      case 1: return adminMenuItems;
      case 2: return lecturerMenuItems;
      case 3: return studentMenuItems;
      default: return [];
    }
  };

  return (
    <>
    <header className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 z-50">
      <div className="flex items-center justify-between">
        {/* Mobile Menu Button - All Users */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          {mobileMenuOpen ? <X className="w-6 h-6 text-gray-700" /> : <Menu className="w-6 h-6 text-gray-700" />}
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
          <p className="text-sm text-gray-500">Welcome back, {user?.fullName ? (() => {
            const names = user.fullName.split(' ').filter(n => n);
            // If first part looks like a title (Mr., Ms., Dr., etc.), use the second part
            if (names.length > 1 && /^(Mr|Ms|Mrs|Dr|Prof|Mr\.|Ms\.|Mrs\.|Dr\.|Prof\.)$/i.test(names[0])) {
              return names[1];
            }
            return names[0];
          })() : 'User'}!</p>
        </div>

        <div className="flex items-center gap-4">
                 
          {/* Profile */}
          <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
            {/* Link avatar to profile or admin dashboard */}
            <NavLink
              to={(() => {
                switch (user?.roleId) {
                  case 2: return '/lecturer/profile';
                  case 3: return '/student/profile';
                  case 1: return '/admin';
                  default: return '/';
                }
              })()}
              className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center bg-white hover:opacity-90 transition-opacity"
              aria-label="View profile"
              title="View profile"
            >
              {user?.avatarThumbPath || user?.avatarPath ? (
                <img src={(user.avatarThumbPath || user.avatarPath) ? getAvatarUrl(user.avatarThumbPath || user.avatarPath) : ''} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-medium">{user?.fullName?.charAt(0) || 'U'}</div>
              )}
            </NavLink>
          </div>
        </div>
      </div>
    </header>
    
    {/* Mobile Navigation Menu - All Users */}
    {mobileMenuOpen && (
      <div className="lg:hidden fixed inset-0 bg-black/50 z-[60]" onClick={() => setMobileMenuOpen(false)}>
        <div className="bg-white w-64 h-full shadow-xl flex flex-col overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <nav className="p-4 space-y-1 flex-1">
            {getMenuItems().map((item, index) => (
              <div key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.end}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-indigo-500 text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </NavLink>
                {/* Divider after every 2 items */}
                {(index === 1 || index === 3 || index === 5) && (
                  <div className="h-px bg-gray-200 my-2"></div>
                )}
              </div>
            ))}
          </nav>
          
          {/* Logout button at bottom with divider */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={() => { logout(); setMobileMenuOpen(false); }}
              className="flex items-center gap-3 px-4 py-3 rounded-lg w-full text-red-600 hover:bg-red-50 transition-all"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
